import { useEffect, useMemo, useState } from 'react';
import { hostGetBankAccount, hostSaveBankAccount } from '../api/host';
import { HostSection } from '../components/host/HostLayout';
import { useToast } from '../components/ToastContext';
import INDIA_BANKS from '../data/indiaBanks';

const statusTone = (status) => {
  const value = (status || '').toUpperCase();
  if (value === 'VERIFIED') return 'bg-emerald-50 text-emerald-700 ring-emerald-100';
  if (value === 'REJECTED') return 'bg-rose-50 text-rose-700 ring-rose-100';
  return 'bg-amber-50 text-amber-700 ring-amber-100';
};

const StatusBadge = ({ status }) => (
  <span className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold ring-1 ring-inset ${statusTone(status)}`}>
    {status || 'PENDING'}
  </span>
);

const HostBankAccount = ({ accessToken }) => {
  const toast = useToast();
  const [account, setAccount] = useState(null);
  const [form, setForm] = useState({
    account_holder_name: '',
    bank_name: '',
    account_number: '',
    ifsc: '',
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const maskedHint = useMemo(() => account?.account_number_masked || '', [account]);
  const bankOptions = useMemo(() => {
    const query = form.bank_name.trim().toLowerCase();
    const matches = query
      ? INDIA_BANKS.filter((name) => name.toLowerCase().includes(query))
      : INDIA_BANKS;
    return matches.slice(0, 20);
  }, [form.bank_name]);

  const loadAccount = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await hostGetBankAccount(accessToken);
      if (data && data.id) {
        setAccount(data);
        setForm({
          account_holder_name: data.account_holder_name || '',
          bank_name: data.bank_name || '',
          account_number: '',
          ifsc: data.ifsc || '',
        });
      } else {
        setAccount(null);
      }
    } catch (err) {
      setError(err.message || 'Failed to load bank account');
      toast?.error?.(err.message || 'Failed to load bank account');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAccount();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken]);

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const validate = () => {
    if (!form.account_holder_name.trim()) {
      toast?.error?.('Account holder name is required');
      return false;
    }
    if (!form.bank_name.trim()) {
      toast?.error?.('Bank name is required');
      return false;
    }
    if (!form.account_number.trim()) {
      toast?.error?.('Account number is required');
      return false;
    }
    if (!form.ifsc.trim()) {
      toast?.error?.('IFSC is required');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSaving(true);
    try {
      const saved = await hostSaveBankAccount(accessToken, form);
      setAccount(saved);
      setForm((prev) => ({ ...prev, account_number: '' }));
      toast?.success?.('Bank account submitted for verification');
    } catch (err) {
      toast?.error?.(err.message || 'Failed to save bank account');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <HostSection
        title="Bank Account"
        description="Add or update your bank account details for payouts."
      >
        <div className="p-6 space-y-5">
          {error && (
            <div className="rounded-lg border border-rose-100 bg-rose-50 px-4 py-2 text-sm text-rose-700">
              {error}
            </div>
          )}

          <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
            <div>
              <p className="text-sm font-semibold text-slate-900">Verification status</p>
              <p className="text-xs text-slate-600">
                {account?.status || 'PENDING'}
                {account?.status === 'REJECTED' && account?.rejection_reason
                  ? ` • ${account.rejection_reason}`
                  : ''}
              </p>
            </div>
            <StatusBadge status={account?.status} />
          </div>

          {loading ? (
            <p className="text-sm text-slate-600">Loading bank account...</p>
          ) : (
            <form onSubmit={handleSubmit} className="grid gap-4 rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="grid gap-3 sm:grid-cols-2">
                <label className="text-sm font-semibold text-slate-800">
                  Account holder name
                  <input
                    value={form.account_holder_name}
                    onChange={(e) => handleChange('account_holder_name', e.target.value)}
                    className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none"
                    placeholder="Full name"
                    required
                  />
                </label>
                <label className="text-sm font-semibold text-slate-800">
                  Bank name
                  <input
                    value={form.bank_name}
                    onChange={(e) => handleChange('bank_name', e.target.value)}
                    list="india-banks"
                    className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none"
                    placeholder="Bank name"
                    required
                  />
                </label>
                <datalist id="india-banks">
                  {bankOptions.map((name) => (
                    <option key={name} value={name} />
                  ))}
                </datalist>
              </div>

              <div className="grid gap-3 sm:grid-cols-2">
                <label className="text-sm font-semibold text-slate-800">
                  Account number
                  <input
                    value={form.account_number}
                    onChange={(e) => handleChange('account_number', e.target.value)}
                    className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none"
                    placeholder="Enter full account number"
                    required
                  />
                  {maskedHint && (
                    <span className="mt-1 block text-xs text-slate-500">On file: {maskedHint}</span>
                  )}
                </label>
                <label className="text-sm font-semibold text-slate-800">
                  IFSC
                  <input
                    value={form.ifsc}
                    onChange={(e) => handleChange('ifsc', e.target.value)}
                    className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm uppercase focus:border-emerald-500 focus:outline-none"
                    placeholder="IFSC code"
                    required
                  />
                </label>
              </div>

              <div className="flex items-center justify-between">
                <p className="text-xs text-slate-500">
                  Updating details will reset status to pending for admin verification.
                </p>
                <button
                  type="submit"
                  disabled={saving}
                  className="rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-emerald-700 disabled:opacity-60"
                >
                  {saving ? 'Saving...' : 'Save bank account'}
                </button>
              </div>
            </form>
          )}
        </div>
      </HostSection>
    </div>
  );
};

export default HostBankAccount;
